import React from "react";
import { Bot, UserCheck, ShieldCheck, Lightbulb, CheckCircle2, XCircle } from "lucide-react";

const AUTOMATE = [
  { t: "Reminder e conferme", d: "Invio automatico di promemoria 72/48/24 ore con link per confermare o spostare. Nessuno dovrebbe farlo più a mano." },
  { t: "Profilazione comunicativa", d: "Adattare tono e canale del messaggio al profilo del paziente (chi vuole i dettagli, chi vuole solo l'orario)." },
  { t: "Follow-up programmati", d: "Richiami automatici per controlli periodici, post-operatori e terapie croniche, con date proposte in agenda." },
  { t: "FAQ e informazioni pratiche", d: "Risposte automatiche a domande ricorrenti: preparazione alla visita, documenti, parcheggio, dilatazione pupillare." },
  { t: "Riordino delle informazioni", d: "Trascrizione e organizzazione di note e referti in campi strutturati, pronti per la revisione del medico." },
  { t: "Supporto alla segreteria", d: "Liste d'attesa, recupero slot liberati, smistamento delle richieste per priorità: l'AI prepara, la persona decide." },
];

const HUMAN = [
  { t: "Diagnosi e decisioni cliniche", d: "Ogni output clinico dell'AI è una bozza: la valutazione e la firma restano sempre del medico." },
  { t: "Comunicazioni delicate", d: "Esiti importanti, pazienti fragili, reclami: qui serve una voce umana, non un messaggio automatico." },
  { t: "Eccezioni e casi particolari", d: "Quando il paziente esce dallo schema (urgenze, esigenze speciali), la segreteria prende in mano la conversazione." },
];

const PRIVACY = [
  "Consenso esplicito: il paziente sa quali dati vengono usati e per cosa.",
  "Minimizzazione: si raccoglie solo ciò che serve al processo, niente di più.",
  "Accessi controllati: ogni operatore vede solo i dati necessari al suo ruolo.",
  "Tracciamento: ogni accesso e ogni invio automatico restano registrati.",
  "Supervisione umana: ogni automazione ha un responsabile che la controlla.",
  "Nessuna diagnosi automatica non controllata: l'AI propone, il medico dispone.",
];

const EXAMPLES = [
  "La segreteria apre la mattina con la lista già pronta: non confermati in rosso, lista d'attesa suggerita per i buchi di agenda.",
  "Il paziente riceve un promemoria nel suo canale preferito e sposta la visita da solo: lo slot liberato viene offerto automaticamente a chi era in attesa.",
  "Dopo la visita, il medico detta le conclusioni: l'AI le riordina nei campi giusti e prepara il referto, che il medico rilegge e firma.",
  "Una domanda frequente (\"posso guidare dopo la dilatazione?\") riceve subito la risposta standard approvata dallo studio, senza occupare il telefono.",
];

export default function ExtraAiGuide() {
  return (
    <div data-testid="extra-ai-guide">
      <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "#F43F5E" }}>Guida pratica</div>
      <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="ai-guide-title">AI in studio medico</h1>
      <p className="mt-3 text-sm text-white/70 leading-relaxed">
        Una guida concreta per capire cosa conviene automatizzare, cosa deve restare umano e come usare i dati in modo corretto, senza perdere il rapporto con il paziente.
      </p>

      <div className="mt-6 bg-white rounded-lg p-5 sm:p-6 shadow-xl">
        <div className="flex items-center gap-2.5">
          <Bot size={17} className="text-brand-azure shrink-0" />
          <h2 className="text-sm sm:text-base font-extrabold text-brand-navy">Cosa automatizzare</h2>
        </div>
        <div className="mt-3 grid gap-3">
          {AUTOMATE.map((a) => (
            <div key={a.t} className="flex items-start gap-2.5">
              <CheckCircle2 size={15} className="text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-slate-800">{a.t}</div>
                <div className="text-xs sm:text-sm text-slate-500 leading-relaxed">{a.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 bg-white rounded-lg p-5 sm:p-6 shadow-xl">
        <div className="flex items-center gap-2.5">
          <UserCheck size={17} style={{ color: "#C9A24B" }} className="shrink-0" />
          <h2 className="text-sm sm:text-base font-extrabold text-brand-navy">Cosa lasciare umano</h2>
        </div>
        <div className="mt-3 grid gap-3">
          {HUMAN.map((a) => (
            <div key={a.t} className="flex items-start gap-2.5">
              <XCircle size={15} className="text-rose-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-slate-800">{a.t}</div>
                <div className="text-xs sm:text-sm text-slate-500 leading-relaxed">{a.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 bg-white rounded-lg p-5 sm:p-6 shadow-xl" data-testid="ai-guide-privacy">
        <div className="flex items-center gap-2.5">
          <ShieldCheck size={17} className="text-emerald-600 shrink-0" />
          <h2 className="text-sm sm:text-base font-extrabold text-brand-navy">Privacy e dati: le regole che non si negoziano</h2>
        </div>
        <ul className="mt-3 grid gap-2">
          {PRIVACY.map((p, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              {p}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 bg-white rounded-lg p-5 sm:p-6 shadow-xl">
        <div className="flex items-center gap-2.5">
          <Lightbulb size={17} style={{ color: "#C9A24B" }} className="shrink-0" />
          <h2 className="text-sm sm:text-base font-extrabold text-brand-navy">Esempi concreti in studio</h2>
        </div>
        <ul className="mt-3 grid gap-2">
          {EXAMPLES.map((p, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "#C9A24B" }} />
              {p}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-slate-400 leading-relaxed">
          La regola d'oro: l'AI toglie lavoro ripetitivo, non toglie relazione. Ogni minuto risparmiato sulle attività meccaniche va reinvestito in ascolto del paziente.
        </p>
      </div>
    </div>
  );
}
